import { ApiError } from '@atlassian/conversation-assistant-service-api';
import { getTraceIdFromResponse } from '@atlassian/conversation-assistant-utils/httpUtils';

export const addPath = (baseUrl: string, path: string) => {
	if (path === '') {
		// return base url without trailing slash
		return baseUrl;
	}

	const urlWithTrailingSlash = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
	const pathWithoutLeadingSlash = !path.startsWith('/') ? path : path.substring(1, path.length);
	const url = [urlWithTrailingSlash, pathWithoutLeadingSlash].join('');
	return url;
};

type ErrorResponseJson = {
	error_code?: string;
	error_message?: string;
	message_template?: string;
	errors?: string[];
	errorMessage?: string;
	/* occasionally, BE errors will include a timestamp that we need to normalize */
	timestamp?: string;
};

const removeTimestampFromErrorMessage = (errorBodyJson: ErrorResponseJson): string => {
	const { timestamp: _timestamp, ...rest } = errorBodyJson;

	return JSON.stringify(rest);
};

const processErrorResponse = async ({ response }: { response: Response }): Promise<Response> => {
	let errorCode: string | undefined;
	let outputErrorMessage: string;
	const responseText = await response.text();
	let rawErrorMessage: string | undefined;
	try {
		const errorBodyJson: ErrorResponseJson = JSON.parse(responseText);
		const { error_code, error_message, message_template, errors, errorMessage, timestamp } =
			errorBodyJson;
		rawErrorMessage = errorMessage ?? error_message;

		if (errors?.length) {
			// Use a single error code for now
			errorCode = errors[0];
		} else if (error_code) {
			errorCode = error_code;
		} else {
			errorCode = message_template;
		}

		if (!errorCode || !rawErrorMessage) {
			outputErrorMessage = timestamp
				? removeTimestampFromErrorMessage(errorBodyJson)
				: responseText;
		} else {
			outputErrorMessage = `${errorCode} - ${rawErrorMessage}`;
		}
	} catch {
		outputErrorMessage = responseText;
	}

	throw new ApiError(`[Code ${response.status}] ${outputErrorMessage}`, {
		errorCode,
		statusCode: response.status,
		rawErrorMessage,
		traceId: getTraceIdFromResponse(response),
	});
};

export const processResponse = async ({ response }: { response: Response }): Promise<Response> => {
	if (!response.ok || response.status >= 400) {
		await processErrorResponse({ response });
	}
	return response;
};
